<template>
  <div class="home-page">
    <Header />
    <el-main>
      <!-- 横幅首屏：参考 LiDAR360 通栏产品 Banner -->
      <section class="hero" :style="heroStyle">
        <div class="hero-overlay"></div>
        <div class="hero-inner dca-container">
          <div class="hero-copy">
            <p class="hero-eyebrow">三维点云智能化处理平台</p>
            <h1>
              <span class="hero-title-en">DiCloudAccess</span>
              <span class="hero-title-zh">点云通</span>
            </h1>
            <p class="hero-subtitle">高精度可视化 · 工程级量算</p>
            <p class="hero-lead">
              支持海量点云加载与交互编辑，融合剖面分析、配准对齐、AI 分类与挖填方计算，为测绘、港口疏浚与海洋工程等领域提供专业解决方案。
            </p>
            <div class="hero-buttons">
              <el-button type="primary" size="large" @click="handleExperience">立即体验</el-button>
              <el-button class="dca-btn-ghost" size="large" @click="watchTutorial">观看演示</el-button>
            </div>
          </div>
        </div>
      </section>

      <!-- 核心模块：左右交替大图 + 文案（LiDAR360 模块区常见排版） -->
      <section class="modules">
        <div class="modules-head dca-container">
          <h2>核心功能</h2>
          <p class="section-subtitle">模块化能力，真实软件界面呈现</p>
        </div>

        <article
          v-for="(item, index) in featureShowcases"
          :key="item.id"
          class="module-row"
          :class="{ 'module-row--reverse': index % 2 === 1, 'module-row--alt': index % 2 === 1 }"
        >
          <div class="module-row__inner dca-container">
            <div class="module-copy">
              <div class="module-copy__icon">
                <el-icon><component :is="item.icon" /></el-icon>
              </div>
              <h3>{{ item.title }}</h3>
              <p class="module-copy__desc">{{ item.description }}</p>
              <ul class="module-copy__points">
                <li v-for="point in item.points" :key="point">{{ point }}</li>
              </ul>
            </div>

            <div
              class="module-media"
              @click="handleMediaClick(item)"
            >
              <div class="module-media__frame">
                <video
                  v-if="item.mediaType === 'video'"
                  :src="item.videoUrl"
                  :poster="item.imageUrl"
                  class="module-media__asset"
                  muted
                  loop
                  playsinline
                  autoplay
                  preload="auto"
                  @canplaythrough="persistPlayedVideo(item.videoUrl)"
                ></video>
                <img
                  v-else
                  :src="item.imageUrl"
                  :alt="item.caption"
                  class="module-media__asset"
                  loading="lazy"
                >
                <div v-if="item.mediaType === 'video'" class="module-media__play">
                  <el-icon><VideoPlay /></el-icon>
                </div>
                <span class="module-media__caption">{{ item.caption }}</span>
              </div>
            </div>
          </div>
        </article>
      </section>

      <!-- 行业应用：简洁分列，承接 Hero 行业表述 -->
      <section class="scenarios">
        <div class="dca-container">
          <h2>适用场景</h2>
          <p class="section-subtitle">面向专业工程场景的点云处理与分析</p>
          <div class="scenarios__list">
            <div
              v-for="scenario in scenarios"
              :key="scenario.title"
              class="scenarios__item"
            >
              <h3>{{ scenario.title }}</h3>
              <p>{{ scenario.description }}</p>
            </div>
          </div>
        </div>
      </section>

      <section class="cta" ref="ctaSection">
        <div class="dca-container">
          <h2>准备好开始了吗？</h2>
          <div class="cta-buttons">
            <el-button type="primary" size="large" @click="showRegisterForm = true">免费试用</el-button>
            <el-button class="dca-btn-ghost" size="large" @click="handleDownload">下载安装包</el-button>
          </div>
        </div>
      </section>
    </el-main>

    <el-footer class="footer">
      <p>&copy; 2020-2026 DiCloudAccess 点云通. 保留所有权利。</p>
      <p class="icp-info">沪ICP备2024054094号-2</p>
    </el-footer>

    <el-dialog
      v-model="showRegisterForm"
      width="500px"
      align-center
      class="home-form-dialog home-form-dialog--register"
      destroy-on-close
    >
      <RegisterForm
        title="申请免费试用"
        show-brand
        embedded
        @registration-success="handleRegistrationSuccess"
      />
    </el-dialog>

    <el-dialog
      v-model="showDownloadDialog"
      title="下载软件"
      width="520px"
      top="12vh"
      class="home-form-dialog"
      :close-on-click-modal="false"
    >
      <DownloadArea @close="closeDownloadArea" @download-complete="handleDownloadComplete" />
    </el-dialog>

    <Teleport to="body">
      <div
        v-if="showVideoDialog"
        class="media-lightbox"
        @click="closeVideoDialog"
      >
        <div class="media-lightbox__panel" @click.stop>
          <div class="media-lightbox__header">
            <span class="media-lightbox__title">{{ currentVideoTitle }}</span>
            <button
              type="button"
              class="media-lightbox__close"
              aria-label="关闭"
              @click="closeVideoDialog"
            >
              ×
            </button>
          </div>
          <video
            :src="currentVideo"
            class="media-lightbox__media"
            controls
            autoplay
            playsinline
            preload="auto"
            @canplaythrough="persistPlayedVideo(currentVideo)"
          ></video>
        </div>
      </div>
    </Teleport>

    <Teleport to="body">
      <div
        v-if="showFullScreenDialog"
        class="media-lightbox"
        @click="showFullScreenDialog = false"
      >
        <div class="media-lightbox__panel" @click.stop>
          <div class="media-lightbox__header">
            <span class="media-lightbox__title">{{ currentImage.caption || currentImage.description }}</span>
            <button
              type="button"
              class="media-lightbox__close"
              aria-label="关闭"
              @click="showFullScreenDialog = false"
            >
              ×
            </button>
          </div>
          <img
            :src="currentImage.imageUrl"
            :alt="currentImage.caption || currentImage.description"
            class="media-lightbox__media"
          >
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import RegisterForm from './RegisterForm.vue';
import DownloadArea from './DownloadArea.vue';
import { ElMessage, ElMessageBox } from 'element-plus';
import { useHead } from '@vueuse/head';
import Header from './Header.vue';
import { useWindowSize } from '@vueuse/core';
import { DataAnalysis, Scissor, Cpu, Box, VideoPlay } from '@element-plus/icons-vue';
import { persistPlayedVideo } from '../js/videoCache';

useHead({
  title: 'DiCloudAccess 点云通',
});

const featureShowcases = [
  {
    id: 'viz',
    icon: DataAnalysis,
    title: '高性能点云可视化',
    description: '主视图流畅浏览海量点云，支持单色、高程、分类与真彩色等多种着色，配合标准视角与相机交互快速查看工程特征。',
    points: ['海量点云流畅浏览', '高程 / 分类 / 真彩色着色', '六视图切换与相机重置'],
    mediaType: 'image',
    imageUrl: '/assets/showcase/main.png',
    caption: '点云三维可视化',
  },
  {
    id: 'section',
    icon: Scissor,
    title: '智能剖面分析',
    description: '开启剖面后，主视图顶视裁剪盒与剖面视图侧视联动，便于在局部剖面内编辑、量测与结构校核。',
    points: ['主视图裁剪盒联动', '剖面视图局部洞察', '剖面内选择与量测'],
    mediaType: 'image',
    imageUrl: '/assets/showcase/section.png',
    caption: '剖面分析',
  },
  {
    id: 'ai',
    icon: Cpu,
    title: 'AI 辅助分类',
    description: 'Plus / 企业版支持选中点云后一键自动分类，可配置预处理与模型推理，结果自动回写工程文件。',
    points: ['一键 AI 自动分类', '支持预处理与模型推理', 'Plus / 企业版可用'],
    mediaType: 'image',
    imageUrl: '/assets/showcase/ai-classify.png',
    caption: 'AI 智能分类',
  },
  {
    id: 'volume',
    icon: Box,
    title: '精确体积计算',
    description: '通过区域选择界定范围后计算挖方、填方与挖填差，支持单云量测与双云对比，适用于土方、堆体与工程量统计。',
    points: ['挖方 / 填方 / 总量差', '支持双云对比差值', '可配置网格与统计方法'],
    mediaType: 'video',
    imageUrl: '/assets/showcase/volume.png',
    videoUrl: '/assets/video/case-volume.mp4',
    caption: '案例演示 · 体积计算',
  },
];

const scenarios = [
  {
    title: '测绘勘察',
    description: '高效处理外业点云，支撑地形测绘与成果交付',
  },
  {
    title: '港口疏浚',
    description: '剖面、体积与分类分析，服务施工与质量校核',
  },
  {
    title: '海洋工程',
    description: '面向复杂空间数据的可视化与工程量计算',
  },
];

const heroBg = '/assets/showcase/home.png';
const heroStyle = computed(() => ({
  backgroundImage: `url('${heroBg}')`,
}));
const heroVideo = '/assets/video/demo.mp4';

const { width } = useWindowSize();

const showRegisterForm = ref(false);
const showDownloadDialog = ref(false);
const showVideoDialog = ref(false);
const showFullScreenDialog = ref(false);
const currentVideo = ref('');
const currentVideoTitle = ref('');
const currentImage = ref({});
const ctaSection = ref(null);

const handleDownload = () => {
  if (width.value <= 768) {
    ElMessageBox.alert(
      '软件只能在PC上安装运行。请在电脑上访问我们的网站以下载安装包。',
      '提示',
      {
        confirmButtonText: '确定',
        type: 'warning',
      }
    );
  } else {
    showDownloadDialog.value = true;
  }
};

const handleRegistrationSuccess = () => {
  showRegisterForm.value = false;
};

const checkSoftwareInstalled = () => false;

const launchSoftware = () => {
  window.location.href = 'dicloudaccess://launch';
};

const closeDownloadArea = () => {
  showDownloadDialog.value = false;
};

const handleDownloadComplete = () => {
  ElMessage.success('安装包下载完成');
  showDownloadDialog.value = false;
};

const watchTutorial = () => {
  currentVideo.value = heroVideo;
  currentVideoTitle.value = '点云通演示视频';
  showVideoDialog.value = true;
};

const closeVideoDialog = () => {
  showVideoDialog.value = false;
  currentVideo.value = '';
};

const showFullScreenImage = (item) => {
  currentImage.value = item;
  showFullScreenDialog.value = true;
};

const handleMediaClick = (item) => {
  if (item.mediaType === 'video') {
    currentVideo.value = item.videoUrl;
    currentVideoTitle.value = item.caption;
    showVideoDialog.value = true;
  } else {
    showFullScreenImage(item);
  }
};

const handleExperience = () => {
  if (checkSoftwareInstalled()) {
    launchSoftware();
  } else if (ctaSection.value) {
    ctaSection.value.scrollIntoView({ behavior: 'smooth' });
  }
};
</script>

<style scoped>
.home-page {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background-color: var(--dca-bg);
  color: var(--dca-text);
}

.el-main {
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: 0;
  overflow-x: hidden;
}

/* ---- Hero：通栏 Banner ---- */
.hero {
  position: relative;
  color: var(--dca-text);
  padding: 0;
  border-bottom: 1px solid var(--dca-border);
  background-color: var(--dca-bg-2);
  background-repeat: no-repeat;
  background-position: center center;
  background-size: cover;
  overflow: hidden;
}

.hero-overlay {
  position: absolute;
  inset: 0;
  background:
    linear-gradient(90deg, rgba(8, 12, 20, 0.92) 0%, rgba(8, 12, 20, 0.72) 42%, rgba(8, 12, 20, 0.28) 100%),
    linear-gradient(180deg, rgba(8, 12, 20, 0.2) 0%, rgba(8, 12, 20, 0.55) 100%);
  z-index: 0;
}

.hero-inner {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  min-height: min(72vh, 680px);
  padding-top: 72px;
  padding-bottom: 72px;
}

.hero-copy {
  max-width: 620px;
  text-align: left;
}

.hero-eyebrow {
  margin: 0 0 14px;
  font-size: 0.95em;
  letter-spacing: 0.08em;
  color: var(--dca-accent);
}

.hero h1 {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  margin: 0 0 10px;
}

.hero-title-en {
  font-size: clamp(2.4rem, 4.5vw, 3.4rem);
  font-weight: var(--dca-fw-bold);
  letter-spacing: -0.03em;
  line-height: 1.1;
  color: var(--dca-text);
}

.hero-title-zh {
  font-size: clamp(1.8rem, 3vw, 2.4rem);
  font-weight: var(--dca-fw-medium);
  color: var(--dca-text);
  margin-top: 8px;
}

.hero-subtitle {
  font-size: 1.35em;
  margin: 16px 0 18px;
  font-weight: var(--dca-fw-medium);
  color: var(--dca-text);
}

.hero-lead {
  margin: 0 0 36px;
  font-size: 1.08em;
  line-height: 1.75;
  color: var(--dca-text-muted);
}

.hero-buttons {
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
}

.hero-buttons .el-button {
  padding: 12px 28px;
  font-size: 1.05em;
  min-width: 180px;
}

/* ---- 模块区标题 ---- */
.modules-head {
  padding: 80px 24px 28px;
  text-align: center;
}

.modules-head h2,
.scenarios h2,
.cta h2 {
  margin: 0 0 12px;
  font-size: 2em;
  font-weight: var(--dca-fw-semibold);
  letter-spacing: -0.02em;
  color: var(--dca-text);
}

.section-subtitle {
  margin: 0;
  color: var(--dca-text-muted);
  font-size: 1.05em;
  font-weight: var(--dca-fw-regular);
}

/* ---- 左右交替模块行 ---- */
.module-row {
  padding: 56px 0;
}

.module-row--alt {
  background: var(--dca-bg-2);
}

.module-row__inner {
  display: grid;
  grid-template-columns: minmax(280px, 0.9fr) minmax(0, 1.2fr);
  gap: 48px;
  align-items: center;
}

.module-row--reverse .module-row__inner {
  grid-template-columns: minmax(0, 1.2fr) minmax(280px, 0.9fr);
}

.module-row--reverse .module-copy {
  order: 2;
}

.module-row--reverse .module-media {
  order: 1;
}

.module-copy {
  text-align: left;
}

.module-copy__icon {
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 20px;
  border-radius: 12px;
  background: var(--dca-accent-soft);
  color: var(--dca-accent);
  font-size: 1.5em;
}

.module-copy h3 {
  margin: 0 0 16px;
  font-size: 1.75em;
  font-weight: var(--dca-fw-semibold);
  line-height: 1.3;
  letter-spacing: -0.02em;
  color: var(--dca-text);
}

.module-copy__desc {
  margin: 0 0 22px;
  font-size: 1.05em;
  line-height: 1.75;
  color: var(--dca-text-muted);
}

.module-copy__points {
  margin: 0;
  padding: 0;
  list-style: none;
}

.module-copy__points li {
  position: relative;
  padding: 8px 0 8px 18px;
  color: var(--dca-text);
  line-height: 1.5;
  border-bottom: 1px solid var(--dca-border);
}

.module-copy__points li:last-child {
  border-bottom: none;
}

.module-copy__points li::before {
  content: '';
  position: absolute;
  left: 0;
  top: 50%;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--dca-accent);
  transform: translateY(-50%);
}

.module-media {
  cursor: pointer;
}

.module-media__frame {
  position: relative;
  overflow: hidden;
  border: 1px solid var(--dca-border);
  border-radius: 4px;
  background: var(--dca-surface);
  box-shadow: 0 16px 48px rgba(0, 0, 0, 0.35);
  transition: transform 0.35s ease, box-shadow 0.35s ease, border-color 0.35s ease;
}

.module-media:hover .module-media__frame {
  transform: translateY(-4px);
  border-color: var(--dca-border-strong);
  box-shadow: 0 22px 56px rgba(0, 0, 0, 0.42);
}

.module-media__asset {
  display: block;
  width: 100%;
  aspect-ratio: 16 / 10;
  object-fit: cover;
  object-position: top center;
  background: #0a0e14;
}

.module-media__play {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 72px;
  height: 72px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: var(--dca-accent);
  color: var(--dca-accent-ink);
  font-size: 34px;
  pointer-events: none;
  z-index: 2;
}

.module-media__caption {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  padding: 14px 16px;
  background: linear-gradient(180deg, transparent, rgba(0, 0, 0, 0.75));
  color: #fff;
  font-size: 0.92em;
  text-align: center;
}

/* ---- 适用场景 ---- */
.scenarios {
  padding: 88px 0;
  text-align: center;
  background: var(--dca-bg);
  border-top: 1px solid var(--dca-border);
}

.scenarios .section-subtitle {
  margin-bottom: 48px;
}

.scenarios__list {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0;
  text-align: left;
  border-top: 1px solid var(--dca-border);
  border-bottom: 1px solid var(--dca-border);
}

.scenarios__item {
  padding: 36px 28px;
  border-right: 1px solid var(--dca-border);
}

.scenarios__item:last-child {
  border-right: none;
}

.scenarios__item h3 {
  margin: 0 0 12px;
  font-size: 1.25em;
  color: var(--dca-text);
}

.scenarios__item p {
  margin: 0;
  line-height: 1.7;
  color: var(--dca-text-muted);
}

/* ---- CTA ---- */
.cta {
  padding: 96px 0;
  text-align: center;
  background:
    radial-gradient(900px 400px at 50% 120%, var(--dca-accent-soft), transparent 60%),
    var(--dca-bg-2);
}

.cta-buttons {
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 16px;
  margin-top: 36px;
}

.cta .el-button {
  padding: 12px 28px;
  font-size: 1.05em;
  min-width: 180px;
}

/* ---- Footer ---- */
.footer {
  background-color: var(--dca-surface);
  color: var(--dca-text-muted);
  padding: 28px 0;
  text-align: center;
  border-top: 1px solid var(--dca-border);
}

.footer p {
  margin: 6px 0;
}

.icp-info {
  font-size: 0.9em;
  color: var(--dca-text-faint);
}

/* ---- Lightbox ---- */
.media-lightbox {
  position: fixed;
  inset: 0;
  z-index: 4000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  background: rgba(0, 0, 0, 0.55);
  box-sizing: border-box;
}

.media-lightbox__panel {
  display: flex;
  flex-direction: column;
  width: fit-content;
  max-width: min(92vw, 1200px);
  max-height: 90vh;
  background: #fff;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.25);
}

.media-lightbox__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 12px;
  flex-shrink: 0;
}

.media-lightbox__title {
  font-size: 16px;
  font-weight: 600;
  color: #303133;
  line-height: 1.3;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.media-lightbox__close {
  flex-shrink: 0;
  width: 28px;
  height: 28px;
  border: none;
  border-radius: 4px;
  background: transparent;
  color: #909399;
  font-size: 22px;
  line-height: 1;
  cursor: pointer;
  padding: 0;
}

.media-lightbox__close:hover {
  color: #303133;
  background: #f2f3f5;
}

.media-lightbox__media {
  display: block;
  width: auto;
  max-width: 100%;
  height: auto;
  max-height: calc(90vh - 52px);
  object-fit: contain;
  background: #111;
}

@media (max-width: 992px) {
  .hero-inner {
    min-height: 520px;
    padding-top: 56px;
    padding-bottom: 56px;
  }

  .hero-copy {
    max-width: 100%;
    text-align: center;
  }

  .hero h1 {
    align-items: center;
  }

  .hero-buttons {
    justify-content: center;
  }

  .module-row__inner,
  .module-row--reverse .module-row__inner {
    grid-template-columns: 1fr;
    gap: 28px;
  }

  .module-row--reverse .module-copy,
  .module-row--reverse .module-media {
    order: initial;
  }

  .module-copy {
    text-align: left;
  }

  .scenarios__list {
    grid-template-columns: 1fr;
  }

  .scenarios__item {
    border-right: none;
    border-bottom: 1px solid var(--dca-border);
  }

  .scenarios__item:last-child {
    border-bottom: none;
  }
}

@media (max-width: 768px) {
  .hero {
    padding: 0 16px;
  }

  .hero-inner {
    min-height: 460px;
  }

  .hero-lead {
    font-size: 1em;
  }

  .hero-buttons,
  .cta-buttons {
    flex-direction: column;
    align-items: stretch;
  }

  .hero-buttons .el-button,
  .cta .el-button {
    width: 100%;
  }

  .modules-head {
    padding: 56px 16px 12px;
  }

  .modules-head h2,
  .scenarios h2,
  .cta h2 {
    font-size: 1.7em;
  }

  .module-row {
    padding: 40px 0;
  }

  .module-copy h3 {
    font-size: 1.4em;
  }

  .scenarios,
  .cta {
    padding: 64px 16px;
  }

  :deep(.el-dialog) {
    width: 94vw !important;
    max-width: none !important;
    margin-top: 6vh !important;
  }

  :deep(.el-dialog__body) {
    padding: 10px 14px 14px !important;
    max-height: 72vh;
  }

  .media-lightbox {
    padding: 12px;
  }

  .media-lightbox__panel {
    width: 100%;
    max-width: 92vw;
    max-height: none;
  }

  .media-lightbox__media {
    width: 100%;
    max-height: min(70vh, calc(100dvh - 120px));
  }
}

/* 表单较高时限制弹窗高度，内部滚动，避免超出视口 */
.home-form-dialog :deep(.el-dialog__body) {
  max-height: 76vh;
  overflow-y: auto;
  padding-top: 8px;
}

/* 试用弹窗：内容短则随内容高度，超出则在 body 内滚动，避免撑满或裁切底部提示 */
:deep(.el-dialog.home-form-dialog--register) {
  --register-dialog-max-height: min(86vh, calc(100dvh - 32px));
  display: flex;
  flex-direction: column;
  align-self: center;
  height: auto;
  max-height: var(--register-dialog-max-height);
  overflow: hidden;
}

.home-form-dialog--register :deep(.el-dialog__header) {
  position: absolute;
  top: 0;
  right: 0;
  z-index: 2;
  padding: 0;
  margin: 0;
  border-bottom: none;
  height: 0;
  overflow: visible;
}

.home-form-dialog--register :deep(.el-dialog__headerbtn) {
  top: 12px;
  right: 12px;
  width: 32px;
  height: 32px;
}

.home-form-dialog--register :deep(.el-dialog__body) {
  flex: 0 1 auto;
  min-height: 0;
  padding: 0 !important;
  max-height: var(--register-dialog-max-height);
  overflow-x: hidden;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior: contain;
}

@media screen and (max-width: 768px) {
  :deep(.el-dialog.home-form-dialog--register) {
    --register-dialog-max-height: calc(100vh - 16px);
    --register-dialog-max-height: calc(100dvh - 16px);
    --register-dialog-max-height: calc(100svh - 16px);
    width: 94vw !important;
    margin: auto !important;
  }
}
</style>
